import React from 'react';
import {
  ArrowRight,
  Search,
  LayoutGrid,
  GitBranch,
  TrendingDown,
  Layers,
  BarChart3,
  ChevronRight,
  Check,
} from 'lucide-react';
import { Breadcrumb, SectionBand, CtaBand, ProductLinks, PRODUCT_LOGIN } from './DemoSiteLayout';
import { T, SURFACE, SHADOW, Hero, DemoLink, Band, IconTile, StepMarker, CenterHead, StatStrip, StatementBand } from './siteUI';

/* ---------- data ---------- */

const MODELS = [
  { name: 'Claude 3.5 Sonnet', price: '$0.42', logo: '✳', tint: 'bg-orange-50 text-orange-500' },
  { name: 'DeepSeek V2.5', price: '$0.30', logo: '🐋', tint: 'bg-sky-50' },
  { name: 'GPT-4o', price: '$0.41', logo: '◎', tint: 'bg-slate-100 text-slate-600' },
  { name: 'Gemini 1.5 Pro', price: '$0.35', logo: '✦', tint: 'bg-violet-50 text-violet-600' },
];

const FEATURES = [
  { name: 'Discover', desc: 'Browse every major model in one catalogue, with current pricing and capabilities.', icon: Search },
  { name: 'Compare', desc: 'Put models side by side on quality, latency and cost before you commit.', icon: LayoutGrid },
  { name: 'Route', desc: 'Send each request to the cheapest model that can actually handle it.', icon: GitBranch },
  { name: 'Optimize', desc: 'Cut spend automatically as prices and model performance shift.', icon: TrendingDown },
  { name: 'Integrate', desc: 'One API, one key, consistent SDKs, per-project budgets and limits.', icon: Layers },
  { name: 'Analyze', desc: 'Track usage, cost and quality per model, per project, over time.', icon: BarChart3 },
];

/* A worked example of routing — the page had no data visual, so the central
   claim ("lowest cost") was asserted rather than shown. */
const ROUTING = [
  { task: 'Classify a support ticket', model: 'Haiku 4.5', cost: '$0.004', saving: '−91%' },
  { task: 'Summarise a call transcript', model: 'DeepSeek V2.5', cost: '$0.012', saving: '−73%' },
  { task: 'Draft a customer reply', model: 'GPT-4o mini', cost: '$0.018', saving: '−64%' },
  { task: 'Reason over a contract', model: 'Claude 3.5 Sonnet', cost: '$0.210', saving: '—' },
];

const HOW_STEPS = [
  { n: 1, title: 'Connect', desc: 'One API key. Every model.' },
  { n: 2, title: 'Compare', desc: 'Evaluate quality, speed & cost.' },
  { n: 3, title: 'Route', desc: 'Automatically route to the best option.' },
  { n: 4, title: 'Optimize', desc: 'Reduce cost and improve outcomes.' },
  { n: 5, title: 'Analyze', desc: 'Track usage and get insights.' },
];

const DEV_FEATURES = [
  'One API for 100+ models',
  'Consistent SDKs & tooling',
  'Usage analytics & cost tracking',
  'Rate limits & failover',
  'Secure keys & permissions',
];

const STATS = [
  { value: 'up to 80%', label: 'Cost reduction' },
  { value: '2.4x', label: 'Faster response' },
  { value: '99.9%', label: 'Uptime' },
  { value: '100+', label: 'Models & providers' },
];

/* ---------- page ---------- */

const DemoSiteOrbiter = () => (
  <>
    <Breadcrumb trail={['Home', 'Orbiter']} />

    {/* Hero */}
    <section className="grid grid-cols-1 items-center gap-12 py-8 lg:grid-cols-2">
      <Hero
        title="Orbiter"
        deck={
          <>
            One account.
            <br />
            Every model.
            <br />
            <span className="text-teal-600">Lowest cost.</span>
          </>
        }
        actions={
          <>
            <DemoLink />
            <ProductLinks login={PRODUCT_LOGIN.orbiter} />
          </>
        }
      >
        Discover, compare, use, and optimize all AI models from a single platform.
      </Hero>

      {/* hero card */}
      <div className={`mx-auto w-full max-w-[400px] rounded-2xl bg-white p-5 ring-1 ring-slate-900/5 ${SHADOW.raised}`}>
        <p className="text-[14.5px] font-semibold">Compare models</p>
        <div className="mt-2 divide-y divide-slate-50">
          {MODELS.map(({ name, price, logo, tint }) => (
            <div key={name} className="flex items-center gap-3.5 py-3.5">
              <span className={`flex h-9 w-9 items-center justify-center rounded-full text-[16px] ${tint}`}>
                {logo}
              </span>
              <span className="flex-1 text-[13.5px] font-semibold">{name}</span>
              <span className="text-[13.5px] font-bold">{price}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    <StatementBand sub="One account, one API key, and routing that picks the right model for every call.">
      Every model, <span className="text-teal-600">at the lowest cost.</span>
    </StatementBand>

    {/* Features */}
    <SectionBand tinted title="Everything you need in one platform">
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map(({ name, desc, icon: Icon }) => (
          <div key={name} className={`flex flex-col p-5 ${SURFACE.cardHover}`}>
            <IconTile icon={Icon} />
            <p className="mt-3.5 text-[14.5px] font-semibold">{name}</p>
            <p className={`mt-1.5 ${T.body}`}>{desc}</p>
          </div>
        ))}
      </div>
    </SectionBand>

    {/* Routing, shown rather than claimed - the page asserted "lowest cost"
        with no figures behind it. */}
    <Band
      title="Routing, in practice"
      subtitle="Every request goes to the cheapest model that can actually do the job. Here is what that looks like on four common tasks."
    >
      <div className="mt-8 overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-left">
          <thead>
            <tr>
              {['Task', 'Routed to', 'Cost / call', 'vs. always-frontier'].map((h) => (
                <th key={h} className="border-b border-slate-200 pb-3 text-[11.5px] font-semibold uppercase tracking-wide text-slate-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROUTING.map(({ task, model, cost, saving }) => (
              <tr key={task}>
                <td className="border-b border-slate-100 py-3.5 text-[13.5px] font-medium text-slate-700">{task}</td>
                <td className="border-b border-slate-100 py-3.5 text-[13.5px] text-slate-600">{model}</td>
                <td className={`border-b border-slate-100 py-3.5 text-[13.5px] ${T.figure}`}>{cost}</td>
                <td className="border-b border-slate-100 py-3.5 text-[13.5px] font-semibold text-teal-600">{saving}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Band>

    {/* How it works */}
    <SectionBand tinted title="How Orbiter works">
      <div className="mt-8 flex flex-wrap items-start justify-center gap-2 md:gap-3">
        {HOW_STEPS.map(({ n, title, desc }, i) => (
          <React.Fragment key={n}>
            {i > 0 && <ChevronRight size={15} className="mt-3 shrink-0 text-slate-300" />}
            <div className="flex w-[140px] flex-col items-center text-center">
              <StepMarker n={n} />
              <p className="mt-2.5 text-[13.5px] font-semibold leading-tight">{title}</p>
              <p className={`mt-1.5 ${T.micro}`}>{desc}</p>
            </div>
          </React.Fragment>
        ))}
      </div>
    </SectionBand>

    {/* Developers */}
    <section className={`grid grid-cols-1 items-center gap-10 px-8 py-10 md:px-12 lg:grid-cols-2 ${SURFACE.band}`}>
      <div>
        <h2 className={T.h2}>Built for developers and teams</h2>
        <ul className="mt-6 space-y-3.5">
          {DEV_FEATURES.map((f) => (
            <li key={f} className="flex items-center gap-3 text-[14px] text-slate-600">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                <Check size={12} strokeWidth={3} />
              </span>
              {f}
            </li>
          ))}
        </ul>
      </div>
      <div className={`overflow-x-auto rounded-2xl bg-slate-900 p-6 font-mono text-[12.5px] leading-relaxed ${SHADOW.raised}`}>
        <p>
          <span className="text-teal-300">POST</span>{' '}
          <span className="text-slate-300">https://api.orbiter.ai/v1/chat/completions</span>
        </p>
        <p>
          <span className="text-amber-300">Authorization:</span>{' '}
          <span className="text-slate-300">Bearer YOUR_API_KEY</span>
        </p>
        <p className="text-slate-400">{'{'}</p>
        <p className="pl-4">
          <span className="text-sky-300">"model"</span>
          <span className="text-slate-400">: </span>
          <span className="text-emerald-300">"gpt-4o"</span>
          <span className="text-slate-400">,</span>
        </p>
        <p className="pl-4">
          <span className="text-sky-300">"messages"</span>
          <span className="text-slate-400">: [</span>
        </p>
        <p className="pl-8">
          <span className="text-slate-400">{'{ '}</span>
          <span className="text-sky-300">"role"</span>
          <span className="text-slate-400">: </span>
          <span className="text-emerald-300">"user"</span>
          <span className="text-slate-400">, </span>
          <span className="text-sky-300">"content"</span>
          <span className="text-slate-400">: </span>
          <span className="text-emerald-300">"Hello"</span>
          <span className="text-slate-400">{' }'}</span>
        </p>
        <p className="pl-4 text-slate-400">]</p>
        <p className="text-slate-400">{'}'}</p>
      </div>
    </section>

    {/* Stats */}
    <section className={`px-6 py-10 md:px-10 ${SURFACE.band}`}>
      <CenterHead title="Lower costs. Higher performance." />
      <div className="mt-8">
        <StatStrip stats={STATS} />
      </div>
    </section>

    <CtaBand
      title="Start building with Orbiter."
      subtitle="Book a demo and we'll route your workload across the models it should be using."
      primary="Book a demo"
    />
  </>
);

export default DemoSiteOrbiter;
